export const CASE_STUDY_BLOCKS = [
  {
    id: 'strengths',
    title: 'Potencialidades e interesses',
    prompt: 'Registre o que mobiliza o estudante, o que ele já faz e onde participa melhor.',
    questions: [
      'Em quais atividades o estudante participa bem?',
      'Quais interesses podem favorecer engajamento e aprendizagem?',
      'O que já realiza com autonomia?'
    ]
  },
  {
    id: 'barriers',
    title: 'Barreiras prioritárias',
    prompt: 'Descreva a barreira observável e o contexto em que ela aparece, sem reduzir o estudante ao diagnóstico.',
    questions: [
      'A barreira está no material, na comunicação, no ambiente, no tempo ou na organização?',
      'Em qual rotina ela limita participação ou aprendizagem?',
      'O que muda quando o contexto é ajustado?'
    ]
  },
  {
    id: 'communication',
    title: 'Comunicação e acessibilidade',
    prompt: 'Registre como o estudante compreende, se expressa e acessa as atividades.',
    questions: [
      'Quais formas de comunicação são usadas e respeitadas?',
      'Quais recursos de acessibilidade ou tecnologia assistiva são necessários?',
      'Há situações de sobrecarga sensorial que pedem ajuste?'
    ]
  },
  {
    id: 'strategies',
    title: 'Estratégias que já funcionam',
    prompt: 'Registre apoios eficazes e como eles ampliam participação sem retirar autonomia.',
    questions: [
      'Qual apoio torna a instrução mais compreensível?',
      'Que organização, material ou mediação já produziu uma resposta melhor?',
      'Em quais pessoas, ambientes e tarefas a estratégia funciona?'
    ]
  }
];

export const CASE_STUDY_SOURCES = [
  { id: 'aee_teacher', label: 'Professor do AEE' },
  { id: 'student', label: 'Estudante' },
  { id: 'family', label: 'Família' },
  { id: 'regular_teacher', label: 'Professor da sala comum' },
  { id: 'school_team', label: 'Equipe escolar' },
  { id: 'observation', label: 'Observação direta' },
  { id: 'documents', label: 'Documentos pertinentes' }
];

const blockIds = new Set(CASE_STUDY_BLOCKS.map((block) => block.id));

function uniqueStrings(value) {
  return [...new Set((Array.isArray(value) ? value : []).map((item) => String(item || '').trim()).filter(Boolean))];
}

function normalizeBlock(raw = {}) {
  return {
    summary: String(raw.summary ?? raw.resumo ?? '').trim(),
    notes: String(raw.notes ?? raw.notas ?? '').trim(),
    sources: uniqueStrings(raw.sources ?? raw.fontes),
    updatedAt: String(raw.updatedAt ?? raw.atualizadoEm ?? ''),
    updatedBy: String(raw.updatedBy ?? raw.atualizadoPor ?? '')
  };
}

export function normalizeCaseStudy(raw = {}, studentId = '') {
  const resolvedStudentId = String(raw.studentId ?? raw.alunoId ?? studentId ?? '');
  const rawBlocks = raw.blocks ?? raw.blocos ?? {};
  const blocks = Object.fromEntries(CASE_STUDY_BLOCKS.map(({ id }) => [id, normalizeBlock(rawBlocks[id])]));
  return {
    ...raw,
    id: String(raw.id || `case_${resolvedStudentId}`),
    studentId: resolvedStudentId,
    blocks,
    updatedAt: String(raw.updatedAt ?? raw.atualizadoEm ?? '')
  };
}

export function caseStudyForStudent(caseStudies = [], studentId = '') {
  const found = (Array.isArray(caseStudies) ? caseStudies : []).find((study) => String(study?.studentId ?? study?.alunoId) === String(studentId));
  return normalizeCaseStudy(found, studentId);
}

export function saveCaseStudyBlock(caseStudies = [], options = {}) {
  const { studentId, blockId, summary = '', notes = '', sources = [], authorId = '', updatedAt = new Date().toISOString() } = options;
  if (!studentId) throw new Error('studentId é obrigatório.');
  if (!blockIds.has(blockId)) throw new Error('Bloco de estudo de caso inválido.');

  const current = caseStudyForStudent(caseStudies, studentId);
  const nextStudy = {
    ...current,
    blocks: {
      ...current.blocks,
      [blockId]: normalizeBlock({ summary, notes, sources, updatedAt, updatedBy: authorId })
    },
    updatedAt
  };
  const currentIndex = (Array.isArray(caseStudies) ? caseStudies : []).findIndex((study) => String(study?.studentId ?? study?.alunoId) === String(studentId));
  if (currentIndex < 0) return [...(Array.isArray(caseStudies) ? caseStudies : []), nextStudy];
  return caseStudies.map((study, index) => index === currentIndex ? nextStudy : study);
}

export function caseStudyProgress(study = {}) {
  const normalized = normalizeCaseStudy(study, study?.studentId);
  const completed = CASE_STUDY_BLOCKS.filter(({ id }) => normalized.blocks[id].summary.length > 0).length;
  return { completed, total: CASE_STUDY_BLOCKS.length, percentage: Math.round((completed / CASE_STUDY_BLOCKS.length) * 100) };
}

export function studentRegistrationContext(student = {}) {
  const interests = Array.isArray(student.interesses) ? student.interesses.join(', ') : String(student.interesses || '');
  return [
    { id: 'interests', label: 'Interesses já cadastrados', value: interests },
    { id: 'communication', label: 'Comunicação já cadastrada', value: String(student.nivelComunicacao || '') },
    { id: 'sensory', label: 'Perfil sensorial já cadastrado', value: String(student.perfilSensorial || '') }
  ].filter((item) => item.value.trim());
}
